import { apiClient } from "@/shared/api/client";
import type { EvaluationScore, PrincipleId, PrincipleSignal } from "@chessla/shared";

export interface PatternExample {
  gameId: string;
  moveNumber: number;
  ply?: number;
  san: string;
  role?: "worked" | "contrast" | "transfer";
  roleReason?: string;
  fenBefore?: string;
  playedFrom?: string;
  playedTo?: string;
  bestMoveSan?: string | null;
  bestMoveFrom?: string | null;
  bestMoveTo?: string | null;
  evalBeforeCp?: number | null;
  evalAfterCp?: number | null;
  evalBeforeMate?: number | null;
  evalAfterMate?: number | null;
  bestContinuation?: string[];
  bestContinuationEvals?: number[];
  bestContinuationScores?: EvaluationScore[] | null;
  centipawnLoss?: number;
  opponent?: string;
  playerRating?: number | null;
  opponentRating?: number | null;
  playerColor?: "w" | "b" | null;
  opening?: string | null;
  playedAt?: string | null;
  evidence?: Record<string, unknown>;
}

export interface GameExample {
  gameId: string;
}

export interface PatternStat {
  motif: string;
  count: number;
  gameCount: number;
  distinctDates?: number;
  averageCpl: number;
  medianCpl?: number;
  decisiveErrorRate?: number;
  lastSeenAt: string | null;
  movement: PatternMovement;
  examples: PatternExample[];
  volume: PatternEvidenceVolume;
}
export type PatternMovement =
  | { status: "insufficient_data"; games: number; neededGames: number }
  | {
      status: "observed";
      classification: "emerging" | "fading" | "persistent" | "uncertain";
      probabilityIncrease: number;
      prior: { events: number; total: 10; mean: number; interval: number[] };
      recent: { events: number; total: 10; mean: number; interval: number[] };
    };
export interface PrinciplePatternExample {
  gameId: string;
  moveNumber: number;
  ply: number;
  san: string;
  fenBefore: string;
  playedFrom: string;
  playedTo: string;
  bestMoveSan: string | null;
  bestMoveFrom: string | null;
  bestMoveTo: string | null;
  centipawnLoss: number;
  opponent: string;
  playerColor: "w" | "b" | null;
  playedAt: string;
  signal: PrincipleSignal;
}
export interface PrinciplePatternStat {
  principle: PrincipleId;
  missedCount: number;
  appliedCount: number;
  affectedGames: number;
  distinctDates: number;
  missedRate: number;
  averageImpactCp: number | null;
  lastSeenAt: string | null;
  examples: PrinciplePatternExample[];
  volume: PatternEvidenceVolume;
}

export interface PatternEvidenceVolume {
  affectedGames: number;
  occurrences: number;
  eligibleDecisions: number;
  analyzedGames: number;
  recurrenceRate: number;
  interval95: number[];
  maturity: EvidenceMaturity;
}

export interface PatternStageErrorCounts {
  opening: number;
  middlegame: number;
  late: number;
}

export interface PatternClockHabits {
  rushedSeconds: number;
  timePressureSeconds: number;
  totalMeasuredPlayerMoves: number;
  rushedMoves: number;
  rushedErrorRate: number | null;
  timePressureMoves: number;
  timePressureErrorRate: number | null;
}

export interface PatternTrendCohort {
  games: number;
  errorMoves: number;
  errorMovesPerGame: number;
  averageCpl: number | null;
}

export interface PatternTrendStats {
  cohortSize: number;
  recent: PatternTrendCohort | null;
  prior: PatternTrendCohort | null;
  errorMovesPerGameDelta: number | null;
  averageCplDelta: number | null;
}

export interface PatternOpeningContext {
  opening: string;
  games: number;
  averageCpl: number;
  errorMoves: number;
}

export interface PatternTacticalOpportunityCounts {
  missedCaptures: number;
  missedChecks: number;
}

export interface PatternTacticalVerificationCoverage {
  verifiedGames: number;
  needsReanalysisGames: number;
}

export interface PatternDrillItem extends PatternExample {
  fenBefore: string;
  bestMoveSan: string | null;
  motif: string;
  evidence?: Record<string, unknown>;
}

/** Up to three verified costly positions per summary measurement (absent from responses cached before it shipped). */
export interface PatternInsightExamples {
  phase: { opening: PatternExample[]; middlegame: PatternExample[]; late: PatternExample[] };
  fast: PatternExample[];
  lowClock: PatternExample[];
}

export interface PatternStatsResponse {
  focus: PatternFocus;
  missedTactics: PatternStat[];
  principleSignals?: PrinciplePatternStat[];
  developmentAreas?: DevelopmentArea[];
  evidenceMaturity?: EvidenceMaturity;
  ignoredThreats: number;
  opponentBlunderCount: number;
  cleanGameCount: number;
  cleanGameExample: GameExample | null;
  insightExamples?: PatternInsightExamples;
  steadyReplyCount: number;
  steadyReplyExample: PatternExample | null;
  missedOpportunityExample: PatternExample | null;
  analyzedGameCount: number;
  stageErrorCounts: PatternStageErrorCounts;
  clockHabits: PatternClockHabits;
  recentVsPriorTrend: PatternTrendStats;
  topOpeningContext: PatternOpeningContext | null;
  tacticalOpportunityCounts: PatternTacticalOpportunityCounts;
  tacticalVerification: PatternTacticalVerificationCoverage;
  drills: PatternDrillItem[];
  evidenceWindow?: { maxGames: number; maxAgeDays: number };
}

export type EvidenceMaturity = "observation" | "early" | "developing" | "established";
export interface DevelopmentArea {
  key: "forcing_calculation" | "tactical_geometry" | "piece_safety" | "king_safety";
  label: string;
  description: string;
  occurrences: number;
  affectedGames: number;
  typicalCpl: number;
  decisiveErrorRate: number;
  maturity: EvidenceMaturity;
  patterns: string[];
  volume?: PatternEvidenceVolume;
}

export const patternStatsUrl = (cohort: FocusCohort = "rapid", exactTimeControl?: string, period = "all"): string => {
  const query = new URLSearchParams({ cohort, period });
  if (exactTimeControl) query.set("exactTimeControl", exactTimeControl);
  return `/coach/patterns?${query}`;
};

export const fetchPatternStats = (
  cohort: FocusCohort = "rapid",
  exactTimeControl?: string,
  period = "all",
): Promise<PatternStatsResponse> => apiClient<PatternStatsResponse>(patternStatsUrl(cohort, exactTimeControl, period));

export type FocusCohort = "bullet" | "blitz" | "rapid" | "daily";
export interface FocusCandidate {
  id: string;
  key?: string;
  rank: number;
  title: string;
  type: "tactical" | "clock";
  status: "available" | "proposed" | "dismissed" | "accepted" | "pending" | "inconclusive" | "improved" | "regressed";
  confidence: "observation" | "recurring" | "trending";
  eligibleSampleSize: number;
  /** Later games counted toward this tracking cycle's verdict, out of windowGames. */
  measuredGames: number;
  windowGames: number;
  /** Verdict of this pattern's previous tracking cycle, if any. */
  previousStatus: "improved" | "regressed" | null;
  /** Share of baseline games (tactical) or measured moves (clock) where the pattern occurred. */
  baselineRate: number;
  baselineGames: number;
  /** The same rate over this cycle's measured later games; null until measured. */
  resultRate: number | null;
  /** Relative drop from baselineRate that counts as improved (0.3 = 30% lower). */
  requiredReduction: number;
  startedAt: string | null;
}
/** How the study Focus went in one later game of the same format. */
/** How often a tracked pattern occurred in counted games, one entry per UTC week, oldest first. */
export interface FocusProgress {
  key: string;
  weeks: Array<{ weekStart: string; games: number; missed: number }>;
}

export interface FocusGameResult {
  gameId: string;
  opponent: string;
  playedAt: string;
  result: "clean" | "missed" | "not_counted";
  moveNumber: number | null;
  ply: number | null;
  san: string | null;
}

export interface PatternFocus {
  cohort: FocusCohort;
  signals: Array<{
    type: "tactical" | "clock";
    key: string;
    label: string;
    confidence: "observation" | "recurring" | "trending";
    occurrences: number;
    affectedGames: number;
    measurements: number;
    averageCpl: number;
    /** Evaluation this pattern cost, each occurrence capped at 1000cp. */
    totalLossCp?: number;
    example: PatternExample | null;
  }>;
  /** The study Focus: Chessla's pick until the player chooses one; it stays until its verdict. */
  focus: FocusCandidate | null;
  /** Every proven pattern Chessla is tracking in this format, strongest first. */
  tracked: FocusCandidate[];
  /** The study Focus in each game played since its tracking started, newest first. */
  focusGames: FocusGameResult[];
  /** Every tracked pattern week by week over the last 12 weeks (absent from responses cached before it shipped). */
  progress?: FocusProgress[];
  /** The previous study Focus when its verdict came in the last two weeks. */
  previousFocus: FocusCandidate | null;
}

/** Tracked patterns and the study Focus for one format, without the full Patterns statistics. */
export const fetchPatternFocus = (cohort: FocusCohort, signal?: AbortSignal): Promise<PatternFocus> =>
  apiClient<PatternFocus>(`/patterns/focus/${encodeURIComponent(cohort)}`, { signal });

/** Makes a tracked or emerging pattern the study Focus; Study follows it. */
export const chooseFocus = (cohort: FocusCohort, key: string): Promise<PatternFocus> =>
  apiClient<PatternFocus>(`/patterns/focus/${encodeURIComponent(cohort)}/choose`, {
    method: "POST",
    body: JSON.stringify({ key }),
  });
